if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.#initVideoAutoplay();
        this.#initPausedOverlay();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      #initVideoAutoplay() {
        const list = this.elements.viewer?.querySelector('[id^="Slider-Gallery"]');
        if (!list || !('IntersectionObserver' in window)) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || navigator.connection?.saveData) return;

        const videoObserver = new IntersectionObserver(this.#onVideoIntersection.bind(this), { threshold: 0.6 });
        const observeVideos = () => {
          for (const deferredMedia of list.querySelectorAll('.media-type-video deferred-media')) {
            videoObserver.observe(deferredMedia);
          }
        };

        // Variant changes can add slides to the gallery
        new MutationObserver(observeVideos).observe(list, { childList: true });

        // Wait for window load so a video slide never competes with the LCP image
        if (document.readyState === 'complete') {
          observeVideos();
        } else {
          window.addEventListener('load', observeVideos, { once: true });
        }
      }

      #onVideoIntersection(entries) {
        for (const entry of entries) {
          const deferredMedia = entry.target;

          deferredMedia.inView = entry.isIntersecting;

          if (entry.isIntersecting) {
            deferredMedia.loadContent(false, true);
            const video = deferredMedia.querySelector('video');
            if (!video) continue;

            video.loop = true;
            if (!deferredMedia.classList.contains('is-paused')) this.#autoplayVideo(deferredMedia, video);
          } else {
            const video = deferredMedia.querySelector('video');
            if (!video || video.paused) continue;

            video.dataset.autoPaused = 'true';
            video.pause();
          }
        }
      }

      #autoplayVideo(deferredMedia, video) {
        // iOS Safari can leave a freshly inserted video stuck in a pending play(); a pause/play cycle unsticks it
        const nudge = setTimeout(() => {
          if (!deferredMedia.inView || deferredMedia.classList.contains('is-paused') || video.currentTime > 0) return;

          video.dataset.autoPaused = 'true';
          video.pause();
          video.play().catch(() => {});
        }, 1500);
        video.addEventListener('playing', () => clearTimeout(nudge), { once: true });

        video.play().catch((error) => {
          // Autoplay blocked (e.g. iOS Low Power Mode): show the play overlay so a tap starts it
          if (error.name === 'NotAllowedError') {
            clearTimeout(nudge);
            deferredMedia.classList.add('is-paused');
          }
        });
      }

      // Reshow the poster's play overlay whenever the shopper pauses a gallery video (or it ends)
      #initPausedOverlay() {
        const list = this.elements.viewer?.querySelector('[id^="Slider-Gallery"]');
        if (!list) return;

        // Media events don't bubble, so listen in the capture phase
        list.addEventListener(
          'pause',
          (event) => {
            const video = event.target;
            if (video.nodeName !== 'VIDEO') return;

            // Programmatic pauses are marked before pause() is called, including global pauseAllMedia().
            if (video.dataset.autoPaused === 'true') {
              delete video.dataset.autoPaused;
              return;
            }

            video.closest('deferred-media')?.classList.add('is-paused');
          },
          true
        );

        list.addEventListener(
          'play',
          (event) => {
            if (event.target.nodeName !== 'VIDEO') return;

            event.target.closest('deferred-media')?.classList.remove('is-paused');
          },
          true
        );

        list.addEventListener('click', (event) => {
          const pauseButton = event.target.closest('.deferred-media__pause-button');
          if (pauseButton) {
            pauseButton.closest('deferred-media')?.querySelector('video')?.pause();
            return;
          }

          const pausedMedia = event.target.closest('.deferred-media__poster')?.closest('deferred-media.is-paused');
          pausedMedia?.querySelector('video')?.play().catch(() => {});
        });
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
